import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { IViewMetadata, IGridViewMetadata, PropertyMetadata, RepositoryBulkDeleteArg } from "@tylertech/tyler-api-types";
import { Utilities } from "@tylertech/shared-module";
import
{
    MetadataViewService,
    MetadataEventService,
    MetadataInputSelectService,
    MetadataActionService,
    MetadataService,
    MetadataPropertyService,
    ProviderStorageService
} from "../../services";
import { AugmentedModelObject, RepositoryFormData } from "../../interfaces";
import { CachedRepositoryService } from "@tylertech/core-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { GridEventService, MetadataTableEventArg, ProjectionGridEventArg } from "./gridEvent.service";
import { ObjectHelpers } from "../../utilities";

@Injectable()
export class GridDeleteService
{
    private readonly $commonDialogService = inject(CommonDialogsService);
    private readonly $repositoryService = inject(CachedRepositoryService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $metadataInputSelectService = inject(MetadataInputSelectService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $gridEventService = inject(GridEventService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $providerStorageService = inject(ProviderStorageService);

    private deleteRetrySubject$ = new Subject<void>();
    get deleteRetry(): Observable<void>
    {
        return this.deleteRetrySubject$.asObservable();
    }

    AllowDeleteCheck(
        entityToDelete: any,
        datacontroller: RepositoryFormData,
        model: AugmentedModelObject | AugmentedModelObject[],
        view: IViewMetadata,
        projectionOrServerGrid: boolean,
        isSubProperty: boolean,
        gridData: any[],
        onDelete: () => Promise<any>
    ): Promise<any>
    {
        const self = this;
        if (!self.$metadataViewService.IsGrid(view))
        {
            throw new Error(`AllowDelete is not a supported function of non-grid views.`);
        }
        if (Utilities.IsNullOrUndefined(view.AllowDeleteCheckProperty))
            return this.DeleteEntity(entityToDelete, datacontroller, model, view, projectionOrServerGrid, isSubProperty, gridData, onDelete);
        else
        {
            const recalcProperty = view.AllowDeleteCheckProperty;
            const PropertyMetadata = <PropertyMetadata>{
                Name: recalcProperty.PropertyToChange,
                RecalculateOnChange: this.$metadataPropertyService.getPropertiesToRecalculate(recalcProperty),
                RecalculateWithParent: recalcProperty.RecalculateWithParent,
            };
            const typeName = entityToDelete["$$TypeName"] ? entityToDelete["$$TypeName"] : self.$metadataViewService.GetGridTypeName(view);
            return this.$metadataPropertyService
                .recalculateProperties(
                    typeName,
                    PropertyMetadata,
                    entityToDelete,
                    entityToDelete["$$Parent"],
                    entityToDelete[recalcProperty.PropertyToChange],
                    undefined,
                    false,
                    false,
                    datacontroller
                )
                .then(
                    _x =>
                    {
                        if (entityToDelete[recalcProperty.PropertyToChange])
                            return this.DeleteEntity(entityToDelete, datacontroller, model, view, projectionOrServerGrid, isSubProperty, gridData, onDelete);
                        else
                        {
                            return this.$commonDialogService.showAlertMessage(
                                "This entity is currently in use and cannot be deleted",
                                "Delete Failed")
                                .then(_x =>
                                {
                                    return Promise.reject();
                                });
                        }
                    },
                    self.$metadataActionService.GetVerificationHandler(
                        datacontroller,
                        () => self.AllowDeleteCheck(entityToDelete, datacontroller, model, view, projectionOrServerGrid, isSubProperty, gridData, onDelete),
                        model
                    )
                );
        }
    }

    DeleteEntity(
        entityToDelete: any,
        datacontroller: RepositoryFormData,
        model: AugmentedModelObject | AugmentedModelObject[],
        view: IViewMetadata,
        projectionOrServerGrid: boolean,
        isSubProperty: boolean,
        gridData: any[],
        onDelete?: () => Promise<any>,
        skipRecalculateOnDelete?: boolean
    ): Promise<any>
    {
        const self = this;
        if (!self.$metadataViewService.IsGrid(view))
        {
            throw new Error(`commandFunction is not a supported function of non-grid views.`);
        }
        let deferEntityRemoval = false;
        if (!Utilities.IsNullOrUndefined(entityToDelete["SerializedRowState"]) && entityToDelete.SerializedRowState !== "Added")
        {
            entityToDelete.SerializedRowState = "Deleted";
            if (!projectionOrServerGrid && !isSubProperty)
                datacontroller.Add(
                    self.$metadataViewService.GetGridTypeName(view),
                    entityToDelete,
                    self.$metadataViewService.createFilterString(model, view)
                );
            if (!Utilities.IsNullOrUndefined(onDelete))
            {
                return onDelete()
                    .then(() =>
                    {
                        if (entityToDelete.SerializedRowState === "Deleted")
                        {
                            //in case the repository delete was cancelled on the server
                            if (self.$metadataViewService.IsProjectionGrid(view))
                            {
                                self.$gridEventService.fireUpdateProjectionGridListeners(<ProjectionGridEventArg>{
                                    Entities: [entityToDelete],
                                    ProviderId: (view as IGridViewMetadata)?.GridProperties["$providerId"],
                                });
                            }
                            // If RecalculateOnDelete is used, we need to remove it after the call, otherwise, it will be missing from the recalculate
                            if (view.ImmediateDelete)
                            {
                                if (view.RecalculateOnDelete && projectionOrServerGrid)
                                    deferEntityRemoval = true;
                                else
                                    self.removeFromDataController(entityToDelete, model, datacontroller, view);
                            }
                            if (view["RecalculateOnDelete"])
                            {
                                return self.tryRecalculateOnDelete([entityToDelete], deferEntityRemoval, datacontroller, model, view);
                            }
                        }
                    },
                        _ =>
                        {
                            entityToDelete.SerializedRowState = "";
                        }
                    );
            }
            else if (projectionOrServerGrid)
            {
                self.$gridEventService.fireUpdateProjectionGridListeners(<ProjectionGridEventArg>{
                    Entities: [entityToDelete],
                    ProviderId: view.GridProperties["$providerId"],
                });
            }
        }
        else
        {
            // If RecalculateOnDelete is used, we need to remove it after the call, otherwise, it will be missing from the recalculate
            if (view.RecalculateOnDelete && projectionOrServerGrid && !view.ImmediateDelete)
            {
                deferEntityRemoval = true;
            }
            else
            {
                self.removeFromDataController(entityToDelete, model, datacontroller, view);
            }

            self.$metadataInputSelectService.RemoveOption(view["GridProperties"].ClassMetadata, entityToDelete);
            if (projectionOrServerGrid)
            {
                entityToDelete.SerializedRowState = "Deleted";
                self.$gridEventService.fireUpdateProjectionGridListeners(<ProjectionGridEventArg>{
                    Entities: [entityToDelete],
                    ProviderId: view.GridProperties["$providerId"],
                });
            }
            else
            {
                const index = gridData.indexOf(entityToDelete);
                if (index > -1)
                {
                    gridData.splice(index, 1);
                }

                /* We need to update model's state for sub-property grids so that the changes can be detected when saving  */
                if (view.SubPropertyName
                    && model
                    && model["SerializedRowState"]
                    && model["SerializedRowState"] === "Unchanged")
                {
                    model["SerializedRowState"] = "Modified";
                }
            }
        }
        if (!skipRecalculateOnDelete)
            return self.tryRecalculateOnDelete([entityToDelete], deferEntityRemoval, datacontroller, model, view);
    }

    private tryRecalculateOnDelete(
        entitiesToDelete: any[],
        deferEntityRemoval: boolean,
        datacontroller: RepositoryFormData,
        model: AugmentedModelObject | AugmentedModelObject[],
        view: IViewMetadata
    ): Promise<any>
    {
        const self = this;
        if (self.$metadataViewService.IsGrid(view) && view.RecalculateOnDelete)
        {
            self.$metadataEventService.fireRecalculateWorkingListeners(
                {
                    ShowLoading: false,
                    Recalculating: true,
                },
                true
            );
            return self.$repositoryService
                .RecalculateGridView({
                    Item1: datacontroller,
                    Item2: view["GridId"],
                })
                .then(
                    rdc =>
                    {
                        self.$metadataActionService.ProcessAfterSave(rdc.AfterSaveContext, rdc.Entity, rdc);

                        entitiesToDelete.forEach(entity =>
                        {
                            if (deferEntityRemoval)
                            {
                                self.removeFromDataController(entity, model, datacontroller, view);
                            }
                            datacontroller.Merge(rdc);
                            if ((entity.SerializedRowState === "Added" || view.ImmediateDelete) &&
                                !(
                                    this.$metadataViewService.IsCellEditGrid(view) &&
                                    Object.entries(datacontroller.OtherEntities).length === 0
                                )
                            )
                            {
                                // If the entity was newly added, we can now remove it from the data controller
                                datacontroller.RemoveEntity(self.$metadataViewService.GetGridTypeName(view), entity);
                            }
                        });
                        self.$metadataService.FireGridReload(rdc.Type, true);
                    },
                    self.$metadataActionService.GetVerificationHandler(
                        datacontroller,
                        () => self.tryRecalculateOnDelete(entitiesToDelete, deferEntityRemoval, datacontroller, model, view),
                        model
                    )
                )
                .finally(() =>
                {
                    self.$metadataEventService.fireRecalculateWorkingListeners(
                        {
                            ShowLoading: false,
                            Recalculating: false,
                        },
                        true
                    );
                });
        }
        return Promise.resolve();
    }

    private removeFromDataController(entityToRemove: any, model: AugmentedModelObject | AugmentedModelObject[], datacontroller: RepositoryFormData, view: IGridViewMetadata): void
    {
        datacontroller.Remove(
            this.$metadataViewService.GetGridTypeName(view),
            entityToRemove,
            this.$metadataViewService.createFilterString(model, view)
        );
    }

    BulkDelete(models: any[], datacontroller: RepositoryFormData, model: AugmentedModelObject | AugmentedModelObject[], view: IGridViewMetadata): Promise<any[]>
    {
        if (this.$metadataViewService.IsProjectionGrid(view))
            return this.bulkDeleteById(models, datacontroller, model, view);
        return this.bulkDelete(models, datacontroller, model, view);
    }

    private bulkDeleteById(models: any[], datacontroller: RepositoryFormData, model: AugmentedModelObject | AugmentedModelObject[], view: IGridViewMetadata): Promise<any[]>
    {
        const self = this;
        const type = view.GridProperties["RowTypeName"];
        const isSubProperty = !Utilities.IsNullOrUndefined(view.SubPropertyName);
        const projectionOrServerGrid = view.GridProperties.ServerPaging || (this.$metadataViewService.IsProjectionGrid(view) && !isSubProperty);

        const ids = [];
        models.forEach(x =>
        {
            if (x.SerializedRowState === "Added")
                this.DeleteEntity(x, datacontroller, model, view, projectionOrServerGrid, isSubProperty, this.$providerStorageService.LocalEntitiesStore[view.GridProperties["$providerId"]], undefined, true);
            else
                ids.push(ObjectHelpers.GetId(view.GridProperties.IdProperty, x, 0));
        });

        if (ids.length === 0)
            return Promise.resolve().then(_ =>
            {
                const deletedEntities = models.filter(f => f.SerializedRowState === "Added");
                if (deletedEntities.length > 0)
                    self.maybeRecalculate(deletedEntities, datacontroller, model, view, type);
                return [];
            });

        return this.$repositoryService
            .BulkDeleteById(<RepositoryBulkDeleteArg>{
                Type: type,
                Ids: ids,
                IdProperty: view.GridProperties.IdProperty
            })
            .then(result =>
            {
                if (Utilities.IsNullOrUndefined(view.ImmediateDelete) || view.ImmediateDelete !== true)
                    self.$repositoryService.ClearAll();

                const deletedEntities = models.filter(f => f.SerializedRowState !== "Added" && result.RetryIds.indexOf(ObjectHelpers.GetId(view.GridProperties.IdProperty, f, 0).toString()) < 0);
                if (deletedEntities?.length > 0)
                {
                    deletedEntities.forEach(f => f.SerializedRowState = "Deleted");
                    self.$gridEventService.fireUpdateProjectionGridListeners(<ProjectionGridEventArg>{
                        Entities: deletedEntities,
                        ProviderId: view?.GridProperties["$providerId"],
                    });
                    self.maybeRecalculate(deletedEntities, datacontroller, model, view, type);
                }

                return models.filter(f => result.RetryIds.indexOf(ObjectHelpers.GetId(view.GridProperties.IdProperty, f, 0).toString()) > -1);
            })
            .finally(() =>
            {
                if (view)
                    view["$$Deleting"] = false;
            });
    }

    private bulkDelete(models: any[], datacontroller: RepositoryFormData, model: AugmentedModelObject | AugmentedModelObject[], view: IGridViewMetadata): Promise<any[]>
    {
        const self = this;
        const type = models[0].$$TypeName;
        const isSubProperty = !Utilities.IsNullOrUndefined(view.SubPropertyName);

        models.forEach(x =>
        {
            if (x.SerializedRowState === "Added")
                this.DeleteEntity(x, datacontroller, model, view, view.GridProperties.ServerPaging, isSubProperty, this.$providerStorageService.LocalEntitiesStore[view.GridProperties["$providerId"]], undefined, true);
            else
            {
                x.SerializedRowState = "Deleted";
                if (!view.GridProperties.ServerPaging && !isSubProperty)
                    datacontroller.Add(
                        self.$metadataViewService.GetGridTypeName(view),
                        x,
                        self.$metadataViewService.createFilterString(model, view)
                    );
            }
        });

        if (models.filter(f => f.SerializedRowState !== "Added").length === 0)
            return Promise.resolve().then(_ =>
            {
                const deletedEntities = models.filter(f => f.SerializedRowState === "Added");
                if (deletedEntities.length > 0)
                    self.maybeRecalculate(deletedEntities, datacontroller, model, view, type);
                return [];
            });

        const idProperty = view.GridProperties.IdProperty ? view.GridProperties.IdProperty :
            models[0]["Id"] ? "Id" :
                models[0]["ID"] ? "ID" : null;

        return this.$repositoryService
            .BulkDelete(<RepositoryBulkDeleteArg>{
                Type: type,
                Entities: models.filter(f => f.SerializedRowState !== "Added"),
                IdProperty: idProperty
            })
            .then(result =>
            {
                if (Utilities.IsNullOrUndefined(view.ImmediateDelete) || view.ImmediateDelete !== true)
                    self.$repositoryService.ClearAll();

                const deletedEntities = models.filter(f => f.SerializedRowState !== "Added" && result.RetryEntities.indexOf(f) < 0);
                if (deletedEntities?.length > 0)
                {
                    self.maybeRecalculate(deletedEntities, datacontroller, model, view, type);
                    self.$providerStorageService.SkipLayout[view.GridProperties["$providerId"]] = true;
                    self.$gridEventService.fireLoadMetadataTableListeners(<MetadataTableEventArg>{
                        Type: type,
                        AllSubPropertyGrids: false
                    });
                }

                return result.RetryEntities;
            })
            .finally(() =>
            {
                if (view)
                    view["$$Deleting"] = false;
            });
    }

    private maybeRecalculate(deleted: any[], datacontroller: RepositoryFormData, model: AugmentedModelObject | AugmentedModelObject[], view: IGridViewMetadata, type: string): void
    {
        this.$providerStorageService.SkipLayout[view.GridProperties["$providerId"]] = true;
        if (view.RecalculateOnDelete)
        {
            const isSubProperty = !Utilities.IsNullOrUndefined(view.SubPropertyName);
            const projectionOrServerGrid = view.GridProperties.ServerPaging || (this.$metadataViewService.IsProjectionGrid(view) && !isSubProperty);
            const deferEntityRemoval = view.RecalculateOnDelete && projectionOrServerGrid && !view.ImmediateDelete;
            this.tryRecalculateOnDelete(deleted, deferEntityRemoval, datacontroller, model, view);
        }
        else
        {
            this.$gridEventService.fireLoadMetadataTableListeners(<MetadataTableEventArg>{
                Type: type,
                AllSubPropertyGrids: false
            });
        }

    }

    triggerDeleteRetry(): void
    {
        this.deleteRetrySubject$.next();
    }
}
